/**
 * Template content for migration utility
 * These are embedded here for the one-time migration from file-based to database storage
 */

export const ndaTemplateContent = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<style>
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: 'Times New Roman', Times, serif;
    font-size: 12pt;
    line-height: 1.8;
    color: #1a1a1a;
    max-width: 8.5in;
    margin: 0 auto;
    padding: 0.5in 0.75in;
    background: #fff;
  }

  .header {
    text-align: center;
    margin-bottom: 2em;
    padding-bottom: 1em;
    border-bottom: 2px solid #333;
  }

  .header h2 {
    font-size: 16pt;
    font-weight: bold;
    letter-spacing: 0.05em;
    margin-bottom: 0.5em;
    text-transform: uppercase;
  }

  .section {
    margin-bottom: 1.75em;
  }

  .section p {
    margin-bottom: 1em;
    text-align: justify;
    text-indent: 0;
  }

  .section p:last-child {
    margin-bottom: 0;
  }

  .intro-section {
    margin-bottom: 2em;
  }

  .intro-section p {
    margin-bottom: 0.75em;
  }

  .purpose-section {
    margin-bottom: 2em;
    padding: 0.75em 0;
  }

  .purpose-section p {
    font-weight: 500;
  }

  ol {
    margin-left: 1.5em;
    margin-bottom: 1em;
  }

  ol li {
    margin-bottom: 1.5em;
    padding-left: 0.5em;
  }

  ol li strong {
    display: block;
    margin-bottom: 0.5em;
    font-size: 12pt;
    font-weight: bold;
  }

  ol li p {
    margin-bottom: 0.75em;
    text-indent: 0;
  }

  ol li p:last-child {
    margin-bottom: 0;
  }

  .signature-section {
    margin-top: 3em;
    padding-top: 2em;
    page-break-inside: avoid;
  }

  .signature-section > p {
    margin-bottom: 2em;
    text-align: center;
    font-weight: 500;
  }

  .signature-blocks {
    display: flex;
    justify-content: space-between;
    gap: 3em;
    margin-top: 2em;
  }

  .signature-block {
    flex: 1;
    min-width: 0;
  }

  .signature-block p:first-child {
    margin-bottom: 1.5em;
    font-weight: bold;
  }

  .signature-line {
    border-top: 1px solid #000;
    width: 100%;
    max-width: 250px;
    margin: 2.5em 0 1em 0;
    height: 1px;
  }

  .signature-block p {
    margin-bottom: 0.5em;
    font-size: 11pt;
  }

  .signature-block p:last-child {
    margin-bottom: 0;
  }

  @media print {
    body {
      padding: 0.25in 0.5in;
    }

    .signature-section {
      page-break-inside: avoid;
    }
  }
</style>
</head>
<body>
  <div class="header">
    <h2>NON-DISCLOSURE AGREEMENT</h2>
  </div>

  <div class="section intro-section">
    <p>THIS AGREEMENT is made and entered into as of {{effectiveDate}}, by and between Sentral Consulting and Development Group ("Sentral Group") and {{recipientName}} (collectively, "the Parties").</p>
  </div>

  <div class="section purpose-section">
    <p><strong>Purpose for Disclosure:</strong> {{purposeOfDisclosure}}</p>
  </div>

  <div class="section">
    <p>The Parties hereby agree as follows:</p>
    <ol>
      <li>
        <strong>Definition of Confidential Information:</strong>
        <p>For purposes of this Agreement, "Confidential Information" shall mean any and all non-public information, including, without limitation, technical, developmental, marketing, social media, sales, operational, performance, cost, know-how, business plans, business methods, and process information, disclosed to the Recipient. For convenience, Sentral Group may, but is not required to, mark written Confidential Information with the legend "Confidential" or an equivalent designation.</p>
      </li>

      <li>
        <strong>Obligations Regarding Confidential Information:</strong>
        <p>All Confidential Information disclosed to the Recipient will be used solely for the Business Purpose and for no other purpose whatsoever. The Recipient agrees to keep Sentral Group's Confidential Information confidential and to protect it with the same degree of care with which it protects its own confidential information, but in no event with less than a reasonable degree of care.</p>
        <p>The Recipient may disclose Confidential Information only to its employees, agents, consultants, and contractors on a need-to-know basis, and only if such individuals have executed appropriate written agreements sufficient to enable the Recipient to enforce all provisions of this Agreement. The Recipient shall not make any copies of Sentral Group's Confidential Information except as needed for the Business Purpose. At Sentral Group's request, the Recipient shall return or destroy all Confidential Information (including any copies thereof) and certify such destruction.</p>
      </li>

      <li>
        <strong>Ownership and Rights:</strong>
        <p>All rights, title, and interest in and to the Confidential Information shall remain with Sentral Group or its licensors. Nothing in this Agreement is intended to grant any rights to the Recipient under any patents, copyrights, trademarks, or trade secrets of Sentral Group. ALL CONFIDENTIAL INFORMATION IS PROVIDED "AS IS". SENTRAL GROUP MAKES NO WARRANTIES, EXPRESS, IMPLIED, OR OTHERWISE, REGARDING NON-INFRINGEMENT OF THIRD-PARTY RIGHTS OR ITS ACCURACY, COMPLETENESS, OR PERFORMANCE.</p>
      </li>

      <li>
        <strong>Exclusions from Confidential Information:</strong>
        <p>The obligations and limitations set forth herein regarding Confidential Information shall not apply to information that:</p>
        <p>(a) Is or becomes part of the public domain through no fault of the Recipient; or</p>
        <p>(b) Is lawfully received from a third party without breach of this Agreement or any other confidentiality obligation.</p>
      </li>

      <li>
        <strong>Breach and Remedies:</strong>
        <p>In the event the Recipient breaches this Agreement, or if a breach appears imminent, Sentral Group shall be entitled to all legal and equitable remedies afforded by law and may recover all reasonable costs and attorneys' fees incurred in seeking such remedies. If Confidential Information is sought by any third party, including via subpoena or other court process, the Recipient shall notify Sentral Group in sufficient time to allow Sentral Group to object to or seek court intervention to prevent disclosure.</p>
      </li>

      <li>
        <strong>Governing Law:</strong>
        <p>The validity, construction, and enforceability of this Agreement shall be governed by the laws of {{jurisdiction}}. This Agreement may not be amended except in writing signed by a duly authorized representative of both Parties. This Agreement shall control in the event of a conflict with any other agreement between the Parties with respect to the subject matter hereof.</p>
      </li>
    </ol>
  </div>

  <div class="signature-section">
    <p>IN WITNESS WHEREOF, the Parties have executed this Agreement as of the date first above written.</p>

    <div class="signature-blocks">
      <div class="signature-block">
        <p><strong>Sentral Consulting and Development Group</strong></p>
        <div class="signature-line"></div>
        <p>By: {{sentralSignerName}}</p>
        <p>Title: {{sentralSignerTitle}}</p>
      </div>

      <div class="signature-block">
        <p><strong>{{recipientCompanyName}}</strong></p>
        <div class="signature-line"></div>
        <p>By: {{recipientSignerName}}</p>
        <p>Title: {{recipientSignerTitle}}</p>
      </div>
    </div>
  </div>
</body>
</html>`;

export const professionalServicesTemplateContent = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<style>
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: 'Times New Roman', Times, serif;
    font-size: 12pt;
    line-height: 1.8;
    color: #1a1a1a;
    max-width: 8.5in;
    margin: 0 auto;
    padding: 0.5in 0.75in;
    background: #fff;
  }

  .header {
    text-align: center;
    margin-bottom: 2em;
    padding-bottom: 1em;
    border-bottom: 2px solid #333;
  }

  .header h2 {
    font-size: 16pt;
    font-weight: bold;
    letter-spacing: 0.05em;
    margin-bottom: 0.5em;
    text-transform: uppercase;
  }

  .section {
    margin-bottom: 1.75em;
  }

  .section h3 {
    font-size: 13pt;
    font-weight: bold;
    margin-bottom: 0.75em;
    margin-top: 0;
  }

  .section p {
    margin-bottom: 1em;
    text-align: justify;
  }

  .section p:last-child {
    margin-bottom: 0;
  }

  .intro-section {
    margin-bottom: 2em;
  }

  .intro-section p {
    margin-bottom: 0.75em;
  }

  .party-info {
    margin: 1em 0;
    padding-left: 1em;
  }

  .party-info p {
    margin-bottom: 0.5em;
    line-height: 1.6;
  }

  .party-info strong {
    display: block;
    margin-bottom: 0.5em;
    font-weight: bold;
  }

  .service-list {
    margin: 1em 0 1em 2em;
    padding-left: 0;
  }

  .service-item {
    margin-bottom: 0.75em;
    line-height: 1.8;
  }

  ul {
    margin: 1em 0 1em 2em;
    padding-left: 0;
  }

  ul li {
    margin-bottom: 0.75em;
    line-height: 1.8;
  }

  .signature-section {
    margin-top: 3em;
    padding-top: 2em;
    page-break-inside: avoid;
  }

  .signature-section > p {
    margin-bottom: 2em;
    text-align: center;
    font-weight: 500;
  }

  .signature-blocks {
    display: flex;
    justify-content: space-between;
    gap: 3em;
    margin-top: 2em;
  }

  .signature-block {
    flex: 1;
    min-width: 0;
  }

  .signature-block p:first-child {
    margin-bottom: 1.5em;
    font-weight: bold;
  }

  .signature-line {
    border-top: 1px solid #000;
    width: 100%;
    max-width: 250px;
    margin: 2.5em 0 1em 0;
    height: 1px;
  }

  .signature-block p {
    margin-bottom: 0.5em;
    font-size: 11pt;
  }

  .signature-block p:last-child {
    margin-bottom: 0;
  }

  @media print {
    body {
      padding: 0.25in 0.5in;
    }

    .signature-section {
      page-break-inside: avoid;
    }
  }
</style>
</head>
<body>
  <div class="header">
    <h2>PROFESSIONAL SERVICES AGREEMENT</h2>
  </div>

  <div class="section intro-section">
    <p>This Professional Services Agreement (the "Agreement") is entered into on {{effectiveDate}} by and between:</p>

    <div class="party-info">
      <p><strong>Sentral Consulting and Development Group</strong> ("Contractor")</p>
      <p>Located at: {{contractorAddress}}</p>
      <p>Email: {{contractorEmail}}</p>
      <p>Phone: {{contractorPhone}}</p>
    </div>

    <p style="text-align: center; margin: 1em 0;">and</p>

    <div class="party-info">
      <p><strong>{{clientName}}</strong> ("Client")</p>
      <p>Located at: {{clientAddress}}</p>
      <p>Email: {{clientEmail}}</p>
      <p>Phone: {{clientPhone}}</p>
    </div>

    <p style="margin-top: 1em;">Collectively referred to as the "Parties".</p>
  </div>

  <div class="section">
    <h3>1. Services</h3>
    <p>Contractor agrees to provide the following professional services to Client (the "Services"):</p>

    <div class="service-list">
      <div class="service-item">• Business Consulting Services at $100/hour</div>
      <div class="service-item">• Custom Software Development at $100/hour</div>
      <div class="service-item">• UX Research and Design at $100/hour</div>
      {{#if additionalServices}}
      <div class="service-item">• {{additionalServices}}</div>
      {{/if}}
    </div>

    <p>Specific project scope: {{projectScope}}</p>
  </div>

  <div class="section">
    <h3>2. Term and Timeline</h3>
    <p>This agreement commences on {{startDate}} and will continue until the completion of the Services or termination by either party with 30 days written notice.</p>
    <p>Estimated project duration: {{projectDuration}}</p>
  </div>

  <div class="section">
    <h3>3. Fees and Payment</h3>
    <p>Services will be billed at $100 per hour. Contractor will submit detailed invoices on a bi-weekly basis, including:</p>
    <ul>
      <li>Hours worked per service category</li>
      <li>Description of work completed</li>
      <li>Any additional expenses (with prior approval)</li>
    </ul>
    <p>Payment terms: Net 14 days</p>
    <p>Late payments will incur a 1.5% fee per two-week period.</p>
  </div>

  <div class="section">
    <h3>4. Project Management and Communication</h3>
    <p>The parties agree to:</p>
    <ul>
      <li>Regular status meetings: {{meetingFrequency}}</li>
      <li>Primary communication method: {{communicationMethod}}</li>
      <li>Project management tool: {{projectManagementTool}}</li>
    </ul>
  </div>

  <div class="section">
    <h3>5. Intellectual Property</h3>
    <p>Upon full payment of all fees, Client will own all rights, title, and interest in the deliverables created specifically for Client. Contractor retains ownership of any pre-existing materials, tools, frameworks, and methodologies used in creating the deliverables.</p>
  </div>

  <div class="section">
    <h3>6. Confidentiality</h3>
    <p>Both parties agree to maintain the confidentiality of any proprietary information shared during the course of this engagement. This includes but is not limited to business strategies, client information, technical specifications, and project details.</p>
  </div>

  <div class="section">
    <h3>7. Limitation of Liability</h3>
    <p>Contractor's liability under this agreement shall be limited to the amount of fees paid by Client. Neither party shall be liable for any indirect, incidental, special, or consequential damages.</p>
  </div>

  <div class="signature-section">
    <p>IN WITNESS WHEREOF, the Parties have executed this Agreement as of the date first above written.</p>

    <div class="signature-blocks">
      <div class="signature-block">
        <p><strong>For Contractor:</strong></p>
        <div class="signature-line"></div>
        <p>Name: {{contractorSignerName}}</p>
        <p>Title: {{contractorSignerTitle}}</p>
        <p>Date: {{signatureDate}}</p>
      </div>

      <div class="signature-block">
        <p><strong>For Client:</strong></p>
        <div class="signature-line"></div>
        <p>Name: {{clientSignerName}}</p>
        <p>Title: {{clientSignerTitle}}</p>
        <p>Date: {{signatureDate}}</p>
      </div>
    </div>
  </div>
</body>
</html>`;

export const saasProposalTemplateContent = `<!DOCTYPE html>
<html>
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body {
        font-family: "Times New Roman", Times, serif;
        font-size: 12pt;
        line-height: 1.8;
        color: #1a1a1a;
        max-width: 8.5in;
        margin: 0 auto;
        padding: 0.5in 0.75in;
        background: #fff;
      }
      .header {
        text-align: center;
        margin-bottom: 2em;
        padding-bottom: 1em;
        border-bottom: 2px solid #333;
      }
      .header h2 {
        font-size: 16pt;
        font-weight: bold;
        letter-spacing: 0.05em;
        margin-bottom: 0.5em;
        text-transform: uppercase;
      }
      .meta {
        text-align: center;
        font-size: 11pt;
        color: #333;
      }
      .section { margin-bottom: 1.75em; }
      .section p { margin-bottom: 1em; text-align: justify; text-indent: 0; }
      .section p:last-child { margin-bottom: 0; }
      .intro-section { margin-bottom: 2em; }
      .purpose-section { margin-bottom: 2em; padding: 0.75em 0; }
      .purpose-section p { font-weight: 500; }
      ol { margin-left: 1.5em; margin-bottom: 1em; }
      ol li { margin-bottom: 1.5em; padding-left: 0.5em; }
      ol li strong { display: block; margin-bottom: 0.5em; font-size: 12pt; font-weight: bold; }
      ol li p { margin-bottom: 0.75em; text-indent: 0; }
      ol li p:last-child { margin-bottom: 0; }

      .pricing-table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 0.75em;
        font-size: 11.5pt;
      }
      .pricing-table th,
      .pricing-table td {
        border: 1px solid #333;
        padding: 0.5em 0.6em;
        vertical-align: top;
      }
      .pricing-table th {
        text-align: left;
        background: #f3f3f3;
        font-weight: bold;
      }
      .totals {
        margin-top: 1em;
        display: grid;
        grid-template-columns: 1fr auto;
        gap: 0.25em 1em;
        max-width: 420px;
        margin-left: auto;
        font-size: 11.5pt;
      }
      .totals div { padding: 0.1em 0; }
      .totals .label { text-align: right; }
      .totals .value { text-align: right; min-width: 140px; }
      .totals .grand { font-weight: bold; border-top: 1px solid #333; padding-top: 0.5em; margin-top: 0.5em; }

      .signature-section {
        margin-top: 3em;
        padding-top: 2em;
        page-break-inside: avoid;
      }
      .signature-section > p {
        margin-bottom: 2em;
        text-align: center;
        font-weight: 500;
      }
      .signature-blocks {
        display: flex;
        justify-content: space-between;
        gap: 3em;
        margin-top: 2em;
      }
      .signature-block { flex: 1; min-width: 0; }
      .signature-block p:first-child { margin-bottom: 1.5em; font-weight: bold; }
      .signature-line {
        border-top: 1px solid #000;
        width: 100%;
        max-width: 250px;
        margin: 2.5em 0 1em 0;
        height: 1px;
      }
      .signature-block p { margin-bottom: 0.5em; font-size: 11pt; }
      .signature-block p:last-child { margin-bottom: 0; }

      @media print {
        body { padding: 0.25in 0.5in; }
        .signature-section { page-break-inside: avoid; }
      }
    </style>
  </head>

  <body>
    <div class="header">
      <h2>{{proposalTitle}}</h2>
      <div class="meta">
        <div>Prepared for: {{clientCompanyName}}</div>
        <div>Prepared by: {{providerCompanyName}}</div>
        <div>Date: {{proposalDate}}</div>
      </div>
    </div>

    <div class="section intro-section">
      <p>
        This Software-as-a-Service (SaaS) proposal (“Proposal”) is provided by
        <strong>{{providerCompanyName}}</strong> (“Provider”) to
        <strong>{{clientCompanyName}}</strong> (“Client”) for the delivery of
        <strong>{{productOrProjectName}}</strong> (the “Service”). This Proposal outlines the scope, timeline,
        commercial terms, and operating assumptions for an initial implementation.
      </p>
    </div>

    <div class="section purpose-section">
      <p><strong>Objective:</strong> {{objective}}</p>
    </div>

    <div class="section">
      <p><strong>Summary:</strong> {{executiveSummary}}</p>
    </div>

    <div class="section">
      <p><strong>In Scope (High-Level):</strong></p>
      <ol>
        <li>
          <strong>Platforms</strong>
          <p>{{platformsSummary}}</p>
        </li>
        <li>
          <strong>Core Features</strong>
          <p>{{coreFeatures}}</p>
        </li>
        <li>
          <strong>Administration & Controls</strong>
          <p>{{adminCapabilities}}</p>
        </li>
        <li>
          <strong>Data, Security, and Access</strong>
          <p>{{securityAndAccess}}</p>
        </li>
      </ol>
    </div>

    <div class="section">
      <p><strong>Out of Scope:</strong> {{outOfScope}}</p>
    </div>

    <div class="section">
      <p><strong>Delivery Timeline:</strong></p>
      <ol>
        <li>
          <strong>Project Start</strong>
          <p>
            Work begins upon signature of this Proposal (or execution of the governing agreement) and receipt of the
            initial payment.
          </p>
        </li>
        <li>
          <strong>Initial Delivery</strong>
          <p>{{deliveryTimeline}}</p>
        </li>
        <li>
          <strong>Third-Party Approvals</strong>
          <p>{{thirdPartyApprovals}}</p>
        </li>
      </ol>
    </div>

    <div class="section">
      <p><strong>Pricing & Payment Terms:</strong></p>

      <table class="pricing-table">
        <thead>
          <tr>
            <th style="width: 40%">Item</th>
            <th>Description</th>
            <th style="width: 18%; text-align: right">Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{{lineItem1Name}}</td>
            <td>{{lineItem1Desc}}</td>
            <td style="text-align: right">{{lineItem1Amount}}</td>
          </tr>
          <tr>
            <td>{{lineItem2Name}}</td>
            <td>{{lineItem2Desc}}</td>
            <td style="text-align: right">{{lineItem2Amount}}</td>
          </tr>
          <tr>
            <td>{{lineItem3Name}}</td>
            <td>{{lineItem3Desc}}</td>
            <td style="text-align: right">{{lineItem3Amount}}</td>
          </tr>
        </tbody>
      </table>

      <div class="totals">
        <div class="label">Subtotal:</div>
        <div class="value">{{subtotal}}</div>

        <div class="label">GST ({{gstRate}}):</div>
        <div class="value">{{gstAmount}}</div>

        <div class="label grand">Total:</div>
        <div class="value grand">{{grandTotal}}</div>
      </div>

      <p style="margin-top: 1em;">
        <strong>Payment Schedule:</strong> {{paymentSchedule}}
      </p>
    </div>

    <div class="section">
      <p><strong>Change Management:</strong></p>
      <p>
        Any items outside the scope above will be logged as change requests and may be delivered as iterative feature
        releases after the initial version. Additional work will be completed either at a mutually agreed flat rate or at
        <strong>{{hourlyRate}}</strong>, whichever is deemed mutually beneficial.
      </p>
    </div>

    <div class="section">
      <p><strong>Hosting, Usage, and Ongoing Costs:</strong></p>
      <p>
        Client acknowledges that ongoing infrastructure and operating costs are expected for a production SaaS system.
        Hosting and third-party service costs are the Client’s responsibility. Provider will work cooperatively with the
        Client to maintain transparency on anticipated costs. Usage-based bills will be issued monthly.
      </p>
      <p><strong>Estimated Hosting Notes:</strong> {{hostingNotes}}</p>
    </div>

    <div class="section">
      <p><strong>Assumptions:</strong> {{assumptions}}</p>
    </div>

    <div class="section">
      <p><strong>Acceptance:</strong></p>
      <p>
        If the terms of this Proposal are acceptable, the parties may sign below to indicate acceptance. This Proposal
        may be incorporated into a formal agreement or statement of work.
      </p>
    </div>

    <div class="signature-section">
      <p>IN WITNESS WHEREOF, the Parties have executed this Proposal as of the date first above written.</p>

      <div class="signature-blocks">
        <div class="signature-block">
          <p><strong>{{providerCompanyName}}</strong></p>
          <div class="signature-line"></div>
          <p>By: {{providerSignerName}}</p>
          <p>Title: {{providerSignerTitle}}</p>
        </div>

        <div class="signature-block">
          <p><strong>{{clientCompanyName}}</strong></p>
          <div class="signature-line"></div>
          <p>By: {{clientSignerName}}</p>
          <p>Title: {{clientSignerTitle}}</p>
        </div>
      </div>
    </div>
  </body>
</html>`;
